import { useEffect, useState } from 'react';
import { MarcaIcone } from '@shared/components/ui/MarcaIcone';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { erroNoLinkDeEmail, supabase } from '@shared/auth/supabase';
import s from './Login.module.css';

/**
 * Onde o link de confirmação do e-mail aterrissa.
 *
 * O Supabase já confirmou o e-mail e mandou a sessão no fragmento da URL;
 * o cliente a troca por uma sessão de verdade (detectSessionInUrl). Esta
 * tela espera isso, diz que deu certo e leva a pessoa para onde ela ia.
 *
 * O DESTINO vem na query (`?destino=/marketplace/contratar/...`): é a
 * contratação que ela começou na lâmina antes de se cadastrar. O link
 * abriu em outra aba, onde a intenção guardada na aba original não
 * existe, então a API o pôs no próprio link. Só caminhos do Marketplace
 * são aceitos -- a mesma regra da API, de novo aqui porque a query é
 * editável.
 *
 * SEGUE SOZINHA depois de um instante: quem acabou de confirmar quer
 * continuar, e o botão fica para quem lê mais devagar.
 */

const SEGUE_EM_MS = 2500;

function destinoSeguro(bruto: string | null): string {
  if (bruto && /^\/marketplace(\/[A-Za-z0-9_-]+)*\/?$/.test(bruto)) return bruto;
  return '/';
}

export function EmailConfirmado() {
  const navegar = useNavigate();
  const [params] = useSearchParams();
  const destino = destinoSeguro(params.get('destino'));
  const [temSessao, setTemSessao] = useState<boolean | null>(
    erroNoLinkDeEmail ? false : null);

  // A troca do token pela sessão leva um instante: sem esperar, um link
  // bom apareceria como vencido (o mesmo cuidado de DefinirAcesso).
  useEffect(() => {
    if (erroNoLinkDeEmail) return;
    let vivo = true;
    supabase.auth.getSession().then(({ data }) => {
      if (vivo && data.session) setTemSessao(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, sessao) => {
      if (vivo && sessao) setTemSessao(true);
    });
    // Sem sessão depois de alguns segundos: o link não valeu.
    const prazo = setTimeout(() => { if (vivo) setTemSessao((t) => t ?? false); }, 6000);
    return () => { vivo = false; clearTimeout(prazo); sub.subscription.unsubscribe(); };
  }, []);

  useEffect(() => {
    if (!temSessao) return;
    const t = setTimeout(() => navegar(destino, { replace: true }), SEGUE_EM_MS);
    return () => clearTimeout(t);
  }, [temSessao, destino, navegar]);

  const marca = (
    <div className={s.marca}>
      <MarcaIcone tamanho={32} className={s.ponto} />
      <span className={s.nome}>Heavy Trade</span>
    </div>
  );

  if (temSessao === null) {
    return (
      <div className={`${s.tela} shell`}>
        <div className={s.cartao}>
          {marca}
          <div className={s.sub}>Confirmando seu e-mail…</div>
        </div>
      </div>
    );
  }

  if (!temSessao) {
    return (
      <div className={`${s.tela} shell`}>
        <div className={s.cartao}>
          {marca}
          <h1 className={s.titulo}>Link expirado</h1>
          <p className={s.texto}>
            Este link de confirmação não vale mais: ele dura 24 horas e só
            pode ser usado uma vez. Entre com seu e-mail e senha para pedir
            outro.
          </p>
          <a className={`${s.botao} ${s.botaoLink} ${s.acao}`} href="/entrar">
            Ir para o login
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className={`${s.tela} shell`}>
      <div className={s.cartao}>
        {marca}
        <div className={s.selado} aria-hidden>
          <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor"
               strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12.5 10 17.5 19 7" />
          </svg>
        </div>
        <h1 className={s.titulo}>E-mail confirmado</h1>
        <p className={s.texto} role="status">
          Sua conta está ativa.{' '}
          {destino === '/'
            ? 'Levando você para a plataforma…'
            : 'Levando você de volta para a contratação…'}
        </p>
        <button type="button" className={`${s.botao} ${s.acao}`}
                onClick={() => navegar(destino, { replace: true })}>
          Continuar
        </button>
      </div>
    </div>
  );
}
